import {
  Button,
  closeCurrentDialog,
  openSuccessDialog,
  showNotification,
  Tooltip,
  useDialog,
} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {
  SourcingVehicleListItemResponseBody,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useAuctionPushVehiclesToSaleMutation,
  useSourcingBulkCreateUserCarvagoBuyMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkDeleteUserToBuyMutation,
  useSourcingPushVehiclesToSaleMutation,
} from '@dms/api/core';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {getSafely, isNilOrZero, Nullish} from 'shared/web';

import {useExportSourcingUserList} from '../../../hooks/useExportSourcingUserList';
import {useSourcingPage} from '../../../hooks/useSourcingPage';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {SourcingPage} from '../../../types/SourcingPage';
import {getCarvagoBuyDisabledTooltipLabel} from '../../../utils/getCarvagoBuyDisabledTooltipLabel';
import {getSourcingUserListByPage} from '../../../utils/getSourcingUserListByPage';
import {openCarvagoTransactionWorkspace} from '../../../utils/openCarvagoTransactionWorkspace';
import {SourcingCarvagoBuyDialog} from '../../SourcingCarvagoBuyDialog/SourcingCarvagoBuyDialog';
import {SourcingUserListExportButton} from '../../SourcingUserListExportButton/SourcingUserListExportButton';
import {classifyCarvagoBuyRows} from '../utils/classifyCarvagoBuyRows';

export type DataViewBulkActionDrawerProps = {
  selectedRows: SourcingVehicleListItemResponseBody[];
  onUnselectSelectedRows: (rowIds: string[]) => void;
  hasUnifiedSurfaces?: boolean;
  /** The table's current sort, so an exported selection reads in the order on screen. */
  orderBy?: string[] | Nullish;
};

const getButtonTranslations = (page: SourcingPage) =>
  match(page)
    .with(SourcingPage.DEFAULT, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.addToComparison'),
      favourites: i18n.t('general.actions.addToFavourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.CART_PAGE, () => ({
      buy: i18n.t('general.actions.removeFromBasket'),
      comparison: i18n.t('general.actions.comparison'),
      favourites: i18n.t('general.actions.favourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: i18n.t('general.actions.buyNow'),
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.HIDDEN_PAGE, () => ({
      buy: null as unknown,
      comparison: null as unknown,
      favourites: null as unknown,
      hide: i18n.t('general.actions.removeFromHidden'),
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }))
    .with(SourcingPage.FAVORITES_PAGE, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.comparison'),
      favourites: i18n.t('general.actions.removeFromFavourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: i18n.t('general.actions.addToCarvagoBuy'),
    }))
    .with(SourcingPage.CARVAGO_BUY_PAGE, () => ({
      buy: i18n.t('general.actions.buy'),
      comparison: i18n.t('general.actions.comparison'),
      favourites: i18n.t('general.actions.favourites'),
      hide: i18n.t('general.actions.hide'),
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }))
    .otherwise(() => ({
      buy: null as unknown,
      comparison: null as unknown,
      favourites: null as unknown,
      hide: null as unknown,
      buyNow: null as unknown,
      carvagoBuy: null as unknown,
    }));

// eslint-disable-next-line max-statements -- Pre-existing violation
export function DataViewBulkActionDrawer(props: DataViewBulkActionDrawerProps) {
  const [createSourcingFavourite, {isLoading: isCreatingSourcingFavourite}] =
    useSourcingBulkCreateUserFavouriteMutation();
  const [deleteSourcingFavourite, {isLoading: isDeletingSourcingFavourite}] =
    useSourcingBulkDeleteUserFavouriteMutation();
  const [createAuctionFavourite, {isLoading: isCreatingAuctionFavourite}] =
    useAuctionBulkCreateUserFavouriteMutation();
  const [deleteAuctionFavourite, {isLoading: isDeletingAuctionFavourite}] =
    useAuctionBulkDeleteUserFavouriteMutation();
  const [createSourcingComparison, {isLoading: isCreatingSourcingComparison}] =
    useSourcingBulkCreateUserComparisonMutation();
  const [createAuctionComparison, {isLoading: isCreatingAuctionComparison}] =
    useAuctionBulkCreateUserComparisonMutation();
  const [createSourcingHidden, {isLoading: isCreatingSourcingHidden}] =
    useSourcingBulkCreateUserHiddenMutation();
  const [deleteSourcingHidden, {isLoading: isDeletingSourcingHidden}] =
    useSourcingBulkDeleteUserHiddenMutation();
  const [createAuctionHidden, {isLoading: isCreatingAuctionHidden}] =
    useAuctionBulkCreateUserHiddenMutation();
  const [deleteAuctionHidden, {isLoading: isDeletingAuctionHidden}] =
    useAuctionBulkDeleteUserHiddenMutation();
  const [createSourcingToBuy, {isLoading: isCreatingSourcingToBuy}] =
    useSourcingBulkCreateUserToBuyMutation();
  const [deleteSourcingToBuy, {isLoading: isDeletingSourcingToBuy}] =
    useSourcingBulkDeleteUserToBuyMutation();
  const [createAuctionToBuy, {isLoading: isCreatingAuctionToBuy}] =
    useAuctionBulkCreateUserToBuyMutation();
  const [deleteAuctionToBuy, {isLoading: isDeletingAuctionToBuy}] =
    useAuctionBulkDeleteUserToBuyMutation();
  const [createSourcingCarvagoBuy, {isLoading: isCarvagoBuyLoading}] =
    useSourcingBulkCreateUserCarvagoBuyMutation();
  const [pushVehiclesToSale, {isLoading: isBuying}] = useSourcingPushVehiclesToSaleMutation();
  const [pushAuctionVehiclesToSale, {isLoading: isBuyingAuctionVehicles}] =
    useAuctionPushVehiclesToSaleMutation();
  const [isCarvagoBuyConfirmOpen, openCarvagoBuyConfirm, closeCarvagoBuyConfirm] = useDialog();

  const page = useSourcingPage();
  const type = useSourcingType();
  const selectedVehiclesCount = props.selectedRows.length;
  const buttonTranslations = getButtonTranslations(page);
  const comparisonButtonLabel = props.hasUnifiedSurfaces
    ? i18n.t('entity.vehicle.labels.compare')
    : buttonTranslations.comparison;
  const carvagoBuyButtonLabel = props.hasUnifiedSurfaces
    ? i18n.t('page.sourcing.labels.carvagoBuyDialogTitle')
    : buttonTranslations.carvagoBuy;
  const {exportUserList, isExporting, canExport} = useExportSourcingUserList(
    getSourcingUserListByPage(page)
  );

  const isCartLoading =
    isCreatingSourcingToBuy ||
    isDeletingSourcingToBuy ||
    isCreatingAuctionToBuy ||
    isDeletingAuctionToBuy;
  const isFavouriteLoading =
    isCreatingSourcingFavourite ||
    isDeletingSourcingFavourite ||
    isCreatingAuctionFavourite ||
    isDeletingAuctionFavourite;
  const isHideLoading =
    isCreatingSourcingHidden ||
    isDeletingSourcingHidden ||
    isCreatingAuctionHidden ||
    isDeletingAuctionHidden;
  const isComparisonLoading = isCreatingSourcingComparison || isCreatingAuctionComparison;

  const getIds = useCallback(
    (
      type: 'favourite' | 'comparison' | 'inCart' | 'hidden' | 'carvagoBuy',
      isChecked = false
    ): string[] =>
      props.selectedRows
        .filter((vehicle) => getSafely(vehicle, type) !== !isChecked)
        .map((vehicle) => vehicle.sourcingVehicle.adId),
    [props.selectedRows]
  );

  const cartButtonIds = getIds('inCart', page === SourcingPage.CART_PAGE);
  const onClickVehicleCartButton = () => {
    if (page === SourcingPage.CART_PAGE) {
      const mutation = type === SourcingType.AUCTIONS ? deleteAuctionToBuy : deleteSourcingToBuy;
      mutation({body: {externalIds: cartButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(cartButtonIds))
        .catch(handleApiError);
    } else {
      const mutation = type === SourcingType.AUCTIONS ? createAuctionToBuy : createSourcingToBuy;
      mutation({body: {externalIds: cartButtonIds}})
        .unwrap()
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToCard')} (${
              cartButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };

  const handleExport = () =>
    exportUserList({
      externalIds: props.selectedRows.map((vehicle) => vehicle.sourcingVehicle.adId),
      orderBy: props.orderBy,
    });

  const handleBuy = () => {
    const mutation =
      type === SourcingType.AUCTIONS ? pushAuctionVehiclesToSale : pushVehiclesToSale;
    mutation({
      externalIds: getIds('inCart', true),
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('entity.vehicle.notifications.vehiclesPushedToSales'))
      )
      .catch(handleApiError);
    props.onUnselectSelectedRows(cartButtonIds);
  };

  const {
    eligibleRows: carvagoBuyEligibleRows,
    skippedInactiveCount: skippedInactiveCarvagoBuyCount,
    hasBlockedVehicle: hasCarvagoBuyBlockedVehicle,
  } = classifyCarvagoBuyRows(props.selectedRows, type);
  const areAllSelectedAlreadyCarvagoBuy =
    props.selectedRows.length > 0 && props.selectedRows.every((vehicle) => vehicle.carvagoBuy);
  const areOnlyInactiveVehiclesEligibleForCarvagoBuy =
    skippedInactiveCarvagoBuyCount > 0 &&
    carvagoBuyEligibleRows.length === 0 &&
    !hasCarvagoBuyBlockedVehicle &&
    !areAllSelectedAlreadyCarvagoBuy;
  const carvagoBuyDisabledTooltipLabel = getCarvagoBuyDisabledTooltipLabel({
    isBlocked: hasCarvagoBuyBlockedVehicle,
    isAlreadyRequested: areAllSelectedAlreadyCarvagoBuy,
    isInactive: areOnlyInactiveVehiclesEligibleForCarvagoBuy,
  });
  const carvagoBuyButtonIds = carvagoBuyEligibleRows.map((vehicle) => vehicle.sourcingVehicle.adId);
  const onClickCarvagoBuyButton = () => {
    openCarvagoBuyConfirm();
  };
  const handleConfirmCarvagoBuy = () => {
    createSourcingCarvagoBuy({
      bulkCreateUserCarvagoBuyRequestBody: {externalIds: carvagoBuyButtonIds},
    })
      .unwrap()
      .then(() => {
        closeCarvagoBuyConfirm();
        openSuccessDialog({
          id: 'sourcing-carvago-buy-bulk-success-dialog',
          heading: i18n.t('entity.sourcing.notifications.carvagoBuySentTitle'),
          subheading: i18n.t('entity.sourcing.notifications.carvagoBuySentToCarvago', {
            count: carvagoBuyButtonIds.length,
          }),
          secondaryButtonLabel: i18n.t('general.actions.trackRequest'),
          onSecondaryButtonClick: openCarvagoTransactionWorkspace,
          buttonLabel: i18n.t('general.actions.close'),
          onClose: closeCurrentDialog,
          'data-testid': testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuySuccessDialog'),
        });
        openCarvagoTransactionWorkspace();
      })
      .catch(handleApiError);
  };

  const CartButton = (
    <Button
      title={`${buttonTranslations.buy} (${cartButtonIds.length})`}
      leftIcon="action/shopping_cart"
      variant={page === SourcingPage.FAVORITES_PAGE ? 'primary' : 'outlined'}
      isDisabled={isNilOrZero(cartButtonIds.length)}
      isLoading={isCartLoading}
      onClick={onClickVehicleCartButton}
      data-testid={testIds.sourcing.classifieds('bulkActionDrawer-cartButton')}
    />
  );

  const favouriteButtonIds = getIds('favourite', page === SourcingPage.FAVORITES_PAGE);
  const createFavourite = (externalIds: string[]) =>
    type === SourcingType.AUCTIONS
      ? createAuctionFavourite({body: {externalIds}})
      : createSourcingFavourite({body: {externalIds}});
  const deleteFavourite = (externalIds: string[]) =>
    type === SourcingType.AUCTIONS
      ? deleteAuctionFavourite({body: {externalIds}})
      : deleteSourcingFavourite({body: {externalIds}});
  const onClickFavouriteButton = () => {
    if (page === SourcingPage.FAVORITES_PAGE) {
      deleteFavourite(favouriteButtonIds)
        .unwrap()
        .then(() => props.onUnselectSelectedRows(favouriteButtonIds))
        .catch(handleApiError);
    } else {
      createFavourite(favouriteButtonIds)
        .unwrap()
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToFavourite')} (${
              favouriteButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };

  const FavouriteButton = (
    <Button
      title={`${buttonTranslations.favourites} (${favouriteButtonIds.length})`}
      leftIcon="toggle/star"
      variant={page === SourcingPage.DEFAULT ? 'primary' : 'outlined'}
      isDisabled={isNilOrZero(favouriteButtonIds.length)}
      isLoading={isFavouriteLoading}
      onClick={onClickFavouriteButton}
      data-testid={testIds.sourcing.classifieds('bulkActionDrawer-favouriteButton')}
    />
  );

  const hiddenButtonIds = getIds('hidden', page === SourcingPage.HIDDEN_PAGE);
  const comparisonButtonIds = getIds('comparison', false);
  const onClickHideButton = () => {
    if (page === SourcingPage.HIDDEN_PAGE) {
      const mutation = type === SourcingType.AUCTIONS ? deleteAuctionHidden : deleteSourcingHidden;
      mutation({body: {externalIds: hiddenButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(hiddenButtonIds))
        .catch(handleApiError);
    } else {
      const mutation = type === SourcingType.AUCTIONS ? createAuctionHidden : createSourcingHidden;
      mutation({body: {externalIds: hiddenButtonIds}})
        .unwrap()
        .then(() => props.onUnselectSelectedRows(hiddenButtonIds))
        .then(() =>
          showNotification.success(
            `${i18n.t('entity.vehicle.notifications.vehiclesAddedToHidden')} (${
              hiddenButtonIds.length
            })`
          )
        )
        .catch(handleApiError);
    }
  };
  const onClickComparisonButton = () => {
    const mutation =
      type === SourcingType.AUCTIONS ? createAuctionComparison : createSourcingComparison;
    mutation({body: {externalIds: comparisonButtonIds}})
      .unwrap()
      .then(() =>
        showNotification.success(
          `${i18n.t('entity.vehicle.notifications.vehiclesAddedToComparison')} (${
            comparisonButtonIds.length
          })`
        )
      )
      .catch(handleApiError);
  };

  return (
    <HStack spacing={2} justify="flex-end" wrap={props.hasUnifiedSurfaces}>
      <Hide when={page === SourcingPage.CART_PAGE}>
        <Button
          title={`${buttonTranslations.hide} (${hiddenButtonIds.length})`}
          leftIcon="action/visibility_off"
          isDisabled={isNilOrZero(hiddenButtonIds.length)}
          isLoading={isHideLoading}
          variant="outlined"
          onClick={onClickHideButton}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-hideButton')}
        />
      </Hide>
      <Hide when={page === SourcingPage.HIDDEN_PAGE}>
        <Button
          title={`${comparisonButtonLabel} (${comparisonButtonIds.length})`}
          variant="outlined"
          leftIcon="action/compare_arrows"
          isDisabled={isNilOrZero(comparisonButtonIds.length)}
          isLoading={isComparisonLoading}
          onClick={onClickComparisonButton}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-comparisonButton')}
        />
        {CartButton}
        {FavouriteButton}
        <Show
          when={type !== SourcingType.AUCTIONS && page !== SourcingPage.CARVAGO_BUY_PAGE}
          whenFeatureEnabled={featureFlags.SOURCING_CARVAGO_BUY}
        >
          <Tooltip
            label={carvagoBuyDisabledTooltipLabel}
            isDisabled={!carvagoBuyDisabledTooltipLabel}
          >
            <Button
              title={`${carvagoBuyButtonLabel} (${carvagoBuyButtonIds.length})`}
              leftIcon="custom/carvago"
              variant="outlined"
              isDisabled={isNilOrZero(carvagoBuyButtonIds.length) || hasCarvagoBuyBlockedVehicle}
              onClick={onClickCarvagoBuyButton}
              data-testid={testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuyButton')}
            />
          </Tooltip>
        </Show>
      </Hide>
      <Show when={canExport}>
        <SourcingUserListExportButton
          actionLabel={i18n.t('general.actions.export')}
          vehiclesCount={selectedVehiclesCount}
          isExporting={isExporting}
          onClick={handleExport}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-exportButton')}
        />
      </Show>
      <Show when={page === SourcingPage.CART_PAGE}>
        <Button
          title={`${buttonTranslations.buyNow} (${selectedVehiclesCount})`}
          variant="primary"
          leftIcon="maps/local_mall"
          isLoading={isBuying || isBuyingAuctionVehicles}
          onClick={handleBuy}
          data-testid={testIds.sourcing.classifieds('bulkActionDrawer-buyNowButton')}
        />
      </Show>
      <SourcingCarvagoBuyDialog
        isOpen={isCarvagoBuyConfirmOpen}
        onClose={closeCarvagoBuyConfirm}
        onConfirm={handleConfirmCarvagoBuy}
        isConfirmLoading={isCarvagoBuyLoading}
        skippedInactiveCount={skippedInactiveCarvagoBuyCount}
        data-testid={testIds.sourcing.classifieds('bulkActionDrawer-carvagoBuyDialog')}
      />
    </HStack>
  );
}
